import {RuleBundleModel} from '../../../../../../models/api/ruleBundleModel';
import {RuleModel} from '../../../../../../models/api/ruleModel';
import {TagModel} from '../../../../../../models/api/tagModel';
import {DelayParameter} from '../../../../../editors/commons/paramModel';
import {checkActionValidity} from '../../../../ruleActionHelpers';
import {checkConditionValidity} from '../../../../ruleConditionHelpers';
import {UnrepliedAfterCondition} from '../../../../ruleConditionModel';
import {RuleIntegration} from '../../../../ruleIntegrationModel';
import {getRuleEditorErrorsForChecks} from '../../../ruleEditorErrors';
import {RuleEditorState, RuleState} from '../../../state/ruleEditorState';
import {SlaEditorErrorsEnum} from '../slaEditorErrors';

export interface SlaTagsModels {
  applies: TagModel;
  breach: TagModel;
  warning: TagModel;
}

export enum SlaEditorSubsetsEnum {
  BREACH = 'breach',
  WARNING = 'warning',
}

export interface SlaEditorSpecificState {
  unreplied: {breach: UnrepliedAfterCondition; warning?: UnrepliedAfterCondition};
  actions: {breach: RuleState['actions']; warning: RuleState['actions']};
  tags: {breach?: number; warning?: number};

  initialRules?: {
    appliesTagRule?: RuleModel;
    breachTagRule: RuleModel;
    breachTimerRule?: RuleModel;
    warningTagRule?: RuleModel;
    removeTagsRule?: RuleModel;
    removeMetadataRule?: RuleModel;
    bundle: RuleBundleModel;
  };
}

export type SlaEditorState = RuleEditorState & {slaState: SlaEditorSpecificState};

export function getSlaEditorErrors(
  state: SlaEditorState,
  ruleIntegration: RuleIntegration,
): ReadonlyArray<SlaEditorErrorsEnum> {
  const ruleEditorErrors = getRuleEditorErrorsForChecks(state, ruleIntegration, [
    'name',
    'conditions',
    'errors',
  ]);

  const slaEditorSpecificErrors = getSlaEditorSpecificErrors(state.slaState, ruleIntegration);

  return [...ruleEditorErrors, ...slaEditorSpecificErrors];
}

function getSlaEditorSpecificErrors(
  state: SlaEditorSpecificState,
  ruleIntegration: RuleIntegration,
): ReadonlyArray<SlaEditorErrorsEnum> {
  const errors: Array<SlaEditorErrorsEnum> = [];

  if (!checkConditionValidity(state.unreplied.breach, ruleIntegration, [])) {
    errors.push(SlaEditorErrorsEnum.INVALID_BREACH_CONDITION);
  }

  if (state.unreplied.warning && !checkConditionValidity(state.unreplied.warning, ruleIntegration, [])) {
    errors.push(SlaEditorErrorsEnum.INVALID_WARNING_CONDITION);
  }

  if (!state.tags.breach) {
    errors.push(SlaEditorErrorsEnum.INVALID_BREACH_ACTION);
  } else if (
    state.actions.breach.some(
      (action) => !checkActionValidity(action, ruleIntegration.findActionDefinition(action), []),
    )
  ) {
    errors.push(SlaEditorErrorsEnum.INVALID_BREACH_ACTION);
  }

  if (state.unreplied.warning) {
    if (!state.tags.warning) {
      errors.push(SlaEditorErrorsEnum.INVALID_WARNING_ACTION);
    } else if (
      state.actions.warning.some(
        (action) => !checkActionValidity(action, ruleIntegration.findActionDefinition(action), []),
      )
    ) {
      errors.push(SlaEditorErrorsEnum.INVALID_WARNING_ACTION);
    } else if (
      state.unreplied.breach.params.delay &&
      state.unreplied.warning.params.delay &&
      !DelayParameter.greatherThan(state.unreplied.breach.params.delay, state.unreplied.warning.params.delay)
    ) {
      errors.push(SlaEditorErrorsEnum.INVALID_WARNING_DELAY);
    }
  }

  return errors;
}
